/**
 * voice-v8 — Persistenter Spiegel der Sicherungs-Aufnahme (Ticket #178/#182).
 *
 * Der VoiceGuard puffert unbestätigte Sprache nur im RAM. Stirbt der Tab,
 * BEVOR flushIfLost() laufen konnte (iOS-Hintergrund, Crash, Reload), war der
 * Puffer weg — die letzte Aussage verloren. Dieses Modul spiegelt die laufende
 * Aufnahme zusätzlich in IndexedDB. Beim nächsten App-Start reicht
 * replayPendingGuard() einen liegen gebliebenen Rest an /api/voice/recover
 * nach — selber Endpunkt + skip_ms-Trim wie der Live-Flush, damit nichts
 * doppelt im Verlauf landet.
 *
 * Alle Funktionen schlucken ihre Fehler: Die Sicherung ist ein Plus, kein
 * Muss — fällt IndexedDB aus, läuft der Voice-Modus unverändert weiter.
 */

const DB_NAME = "voice-guard";
const STORE = "pending";
const KEY = "current";
const MIN_REPLAY_BYTES = 2_000; // darunter: Atmer/Klick, nichts zum Nachreichen

interface GuardSnapshot {
  blob: Blob;
  mime: string;
  skipMs: number;
  reason: string;
  ts: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Eine IndexedDB-Transaktion ausführen und auf ihren Abschluss warten. */
async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  try {
    const db = await openDb();
    return await new Promise<T | null>((resolve) => {
      const tx = db.transaction(STORE, mode);
      const req = run(tx.objectStore(STORE));
      tx.oncomplete = () => {
        db.close();
        resolve((req.result as T) ?? null);
      };
      tx.onerror = () => {
        db.close();
        resolve(null);
      };
    });
  } catch {
    return null;
  }
}

/** Aktuellen unbestätigten Mitschnitt in IndexedDB spiegeln (überschreibt). */
export async function saveGuardSnapshot(snap: GuardSnapshot): Promise<void> {
  await withStore("readwrite", (store) => store.put(snap, KEY));
}

/** Spiegel leeren — Aufnahme wurde rotiert, stummgeschaltet oder zugestellt. */
export async function clearGuardSnapshot(): Promise<void> {
  await withStore("readwrite", (store) => store.delete(KEY));
}

/**
 * Beim App-Start: einen aus einem toten Tab liegen gebliebenen Mitschnitt an
 * /api/voice/recover nachreichen. Gibt true zurück, wenn dabei ein Transkript
 * entstand. Der Spiegel wird in jedem Fall geleert.
 */
export async function replayPendingGuard(): Promise<boolean> {
  const snap = await withStore<GuardSnapshot>("readonly", (store) =>
    store.get(KEY),
  );
  await clearGuardSnapshot();
  if (!snap || !snap.blob || snap.blob.size < MIN_REPLAY_BYTES) return false;
  try {
    const ext = snap.mime.includes("mp4") ? "m4a" : "webm";
    const form = new FormData();
    form.append("audio", snap.blob, `sicherung.${ext}`);
    form.append("reason", `tab-recovery-${snap.reason}`.slice(0, 40));
    form.append("skip_ms", String(Math.max(0, snap.skipMs)));
    const res = await fetch("/api/voice/recover", {
      method: "POST",
      credentials: "same-origin",
      body: form,
    });
    const data = (await res.json().catch(() => null)) as {
      ok?: boolean;
      transcript?: string;
    } | null;
    return !!data?.ok && !!data.transcript;
  } catch {
    return false;
  }
}
